import { computed, inject, Injectable } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router'
import { map, Observable, of, Subject } from 'rxjs'
import { filter } from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { FilterValues, OrderArgs } from '../types/types'
import { PaginationArgs } from '@wm-front/shared/interfaces'
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  SimpleFilterConfiguration,
  SimpleFilterType,
} from '@wm-front/shared/simple-filters'
import { toSignal } from '@angular/core/rxjs-interop'
import { FiltersChangeReason, TabTrackingService } from '@wm-front/core'

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class RoutedFiltersRouteManagerService {
  public _route = inject(ActivatedRoute)

  public queryParams$ = toSignal(this._route.queryParams)

  public childQueryParams$ = toSignal(
    this._route.children?.[0] ? this._route.children?.[0]?.queryParams : of({})
  )

  public currentQueryParams = computed(() =>
    Object.keys(this.childQueryParams$() || {})?.length
      ? this.childQueryParams$()
      : this.queryParams$()
  )

  public reset$ = new Subject<void>()

  private _router = inject(Router)

  private _tabTracking = inject(TabTrackingService)

  private _pageKey = 'page'

  private _paginationKey = 'pagination'

  private _orderKey = 'order'

  private _activeSortKey = 'activeSort'

  private _persistentQueryParamPrefix = 'persistent'

  private _persistentQueryParams: Params = {}

  private _isRestoringPersistentQueryParams = false

  private _lastNavigationPath = this.getPathFromUrl(this._router.url)

  private _notAFilterKeys = [this._pageKey, this._orderKey, this._activeSortKey]

  constructor() {
    this._persistentQueryParams = this.extractPersistentQueryParams(
      this.getCurrentRouteQueryParams()
    )
    this.managePersistentQueryParamsOnNavigation()
  }

  /**
   * Will return an observable return the route params
   */
  public get routeParams$(): Observable<Partial<FilterValues>> {
    const queryParams = Object.keys(
      this._route.children?.[0]?.snapshot.queryParams || {}
    )?.length
      ? this._route.children[0].queryParams
      : this._route.queryParams
    return queryParams.pipe(
      untilDestroyed(this),
      map((params) => this.convertParamsAsFilters(params))
    )
  }

  public get pagination$(): Observable<PaginationArgs> {
    return this.routeParams$.pipe(
      map((params) =>
        params[this._paginationKey]
          ? JSON.parse(params[this._paginationKey] || '{}')
          : null
      )
    )
  }

  public get page$(): Observable<number> {
    return this.routeParams$.pipe(
      map((params) => Number(params[this._pageKey]))
    )
  }

  public get order$(): Observable<OrderArgs[]> {
    return this.routeParams$.pipe(
      map((params) =>
        params[this._orderKey] ? JSON.parse(params[this._orderKey] || '{}') : []
      )
    )
  }

  public get activeSort$(): Observable<string> {
    return this.routeParams$.pipe(
      map((params) => params[this._activeSortKey] || '')
    )
  }

  public get filters$(): Observable<FilterValues> {
    return this.routeParams$.pipe(
      map((params) =>
        Object.keys(params)
          .filter((key) => !this._notAFilterKeys.includes(key))
          .reduce(
            (obj, key) => ({ ...obj, [key]: params[key] }),
            {} as FilterValues
          )
      )
    )
  }

  public get persistentFiltersActive$(): Observable<boolean> {
    return this.filters$.pipe(
      map((filters) =>
        Object.keys(filters).some((key) =>
          key.toLowerCase().includes('persist')
        )
      )
    )
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public filtersSignal = toSignal(this.filters$)

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public numberOfPersistentFilters = computed(() =>
    this.filtersSignal()
      ? (this.filtersSignal()?.persistentBuildingIds?.split(',').length || 0) +
        (this.filtersSignal()?.persistentSupplyManager?.split(',').length ||
          0) +
        (this.filtersSignal()?.persistentAccountIds?.split(',').length || 0) +
        (this.filtersSignal()?.persistentReferentUserIds?.split(',').length ||
          0) +
        (this.filtersSignal()?.persistentManagerUserIds?.split(',').length || 0)
      : 0
  )

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public hasPersistentFilters$ = computed(
    () =>
      this.filtersSignal() &&
      Object.keys(this.filtersSignal() as object).some((key) =>
        key.includes('persist')
      )
  )

  /**
   * Will update the current route with the filters
   * @param filters
   * @param configurations
   */
  public async updateCurrentRouteWithFilters(
    filters: Partial<FilterValues>,
    configurations?: SimpleFilterConfiguration<any>[]
  ): Promise<void> {
    const queryParams = {
      ...this._persistentQueryParams,
      ...this.currentQueryParams(), // For now we only want to add to the URL, and not entirely replace it
      ...this.convertFiltersAsParams(filters, configurations),
    }
    this.syncPersistentQueryParams(queryParams)
    this._tabTracking.noteFiltersChange(
      this.isFiltersReset(filters)
        ? FiltersChangeReason.Reset
        : FiltersChangeReason.User
    )
    await this._router.navigate([], {
      relativeTo: this._route,
      queryParams,
      replaceUrl: true,
    })
  }

  public async updateOrderAndActiveSort(
    order: OrderArgs[] | null,
    activeSort: string
  ): Promise<void> {
    await this.updateRoute({
      [this._activeSortKey]: activeSort,
      [this._orderKey]: order ? JSON.stringify(order) : null,
    })
  }

  public async resetPage(): Promise<void> {
    await this.updatePage(1)
  }

  public async updatePage(page: number): Promise<void> {
    await this.updateRoute({
      [this._pageKey]: page,
    })
  }

  public async updatePagination(
    pagination: PaginationArgs | null
  ): Promise<void> {
    await this.updateRoute({
      [this._paginationKey]: pagination ? JSON.stringify(pagination) : null,
    })
  }

  private async updateRoute(parameter: { [key: string]: any }): Promise<void> {
    const queryParams = {
      ...this._persistentQueryParams,
      ...this.currentQueryParams(),
      ...parameter,
    }
    this.syncPersistentQueryParams(queryParams)
    await this._router.navigate([], {
      relativeTo: this._route,
      queryParams,
      replaceUrl: true,
    })
  }

  private managePersistentQueryParamsOnNavigation(): void {
    this._router.events
      .pipe(
        untilDestroyed(this),
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        map(() => this.getCurrentRouteQueryParams())
      )
      .subscribe(async (queryParams) => {
        const currentPath = this.getPathFromUrl(this._router.url)
        const pathChanged = currentPath !== this._lastNavigationPath
        this._lastNavigationPath = currentPath

        const hasPersistentQueryParams = Object.keys(queryParams).some((key) =>
          this.isPersistentQueryParamKey(key)
        )
        if (hasPersistentQueryParams) {
          this._persistentQueryParams =
            this.extractPersistentQueryParams(queryParams)
          return
        }

        // Same-route query updates that remove persistent params are treated as an explicit reset.
        if (!pathChanged) {
          this._persistentQueryParams = {}
          return
        }

        if (
          this._isRestoringPersistentQueryParams ||
          !Object.keys(this._persistentQueryParams).length
        ) {
          return
        }

        this._isRestoringPersistentQueryParams = true
        try {
          await this._router.navigate([], {
            relativeTo: this._route,
            queryParams: {
              ...queryParams,
              ...this._persistentQueryParams,
            },
            replaceUrl: true,
          })
        } finally {
          this._isRestoringPersistentQueryParams = false
        }
      })
  }

  /**
   * Whether an update only clears filters (the "reset" buttons)
   * @param filters
   * @private
   */
  private isFiltersReset(filters: Partial<FilterValues>): boolean {
    const values = Object.values(filters)
    return (
      values.length > 0 &&
      values.every(
        (value) =>
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && !value.length)
      )
    )
  }

  private getCurrentRouteQueryParams(): Params {
    let route = this._router.routerState.snapshot.root
    while (route?.firstChild) {
      route = route.firstChild
    }
    return route?.queryParams || {}
  }

  private syncPersistentQueryParams(queryParams: Params): void {
    const hasPersistentQueryParams = Object.keys(queryParams).some((key) =>
      this.isPersistentQueryParamKey(key)
    )
    if (hasPersistentQueryParams) {
      this._persistentQueryParams =
        this.extractPersistentQueryParams(queryParams)
    }
  }

  private extractPersistentQueryParams(queryParams: Params): Params {
    return Object.keys(queryParams)
      .filter((key) => this.isPersistentQueryParamKey(key))
      .reduce((persistentQueryParams, key) => {
        persistentQueryParams[key] = queryParams[key]
        return persistentQueryParams
      }, {})
  }

  private isPersistentQueryParamKey(key: string): boolean {
    return key.toLowerCase().startsWith(this._persistentQueryParamPrefix)
  }

  private getPathFromUrl(url: string): string {
    return (url || '').split('?')[0]
  }

  /**
   * Will convert filters value to params
   * @param filters
   * @param configurations
   * @private
   */
  private convertFiltersAsParams(
    filters: Partial<FilterValues>,
    configurations?: SimpleFilterConfiguration<any>[]
  ): Params {
    return Object.keys(filters)
      .filter((key) => filters[key] !== undefined && filters[key] !== null)
      .reduce((obj, key) => {
        const configuration = configurations?.find(
          (configuration) => configuration.key === key
        )
        if (configuration?.type !== SimpleFilterType.EXTERNAL) {
          const currentValue = filters[key]
          const shouldStringifyPeriodValue =
            configuration?.type === SimpleFilterType.PERIOD &&
            currentValue &&
            typeof currentValue !== 'string'
          obj[configuration?.urlName || key] = shouldStringifyPeriodValue
            ? JSON.stringify(currentValue)
            : currentValue?.toString()
        }
        return obj
      }, {})
  }

  /**
   * Will convert params as filters.
   * Same method as above, only to let a bit of space for some specifics in the future
   * @param params
   * @private
   */
  private convertParamsAsFilters(params: Params): Partial<FilterValues> {
    return Object.keys(params)
      .filter(
        (key) =>
          params[key] !== undefined &&
          params[key] !== null &&
          params[key] !== ''
      )
      .reduce((obj, key) => {
        obj[key] = params[key].toString()
        return obj
      }, {}) as Partial<FilterValues>
  }
}
